const { modules } = require('./catalog');
const byId = Object.fromEntries(modules.map(m => [m.id, m]));
const legacyFlags = [...new Set(['IsManager', 'IsDevisManager', ...modules.map(m => m.flag).filter(Boolean)])];
const enumValue = value => value.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();

function legacyAccess(user, definition) {
  return Boolean(user && (user.IsManager || definition.legacyEveryone ||
    (definition.flag && user[definition.flag]) || definition.inheritedFlags.some(flag => user[flag])));
}

// Pure compatibility conversion; never writes business data or changes legacy flags.
function permissionDetails(user, group = false) {
  // Prisma rows are authoritative, including an empty relation (no grants).
  // PermissionSettings is only the API/form DTO, never a database JSON column.
  const persisted = Array.isArray(user?.Permissions);
  const stored = persisted ? {
    consultation: user.ArticleConsultation,
    zoning: user.ArticleZoning,
    modules: Object.fromEntries(modules.map(m => {
      const actions = new Set(user.Permissions.filter(p => p.Module === enumValue(m.id)).map(p => p.Action));
      return [m.id, { full: actions.has('FULL'), actions: Object.fromEntries(m.actions.map(a => [a, actions.has(enumValue(a))])) }];
    })),
  } : (user?.PermissionSettings || {});
  const result = { version: 1, consultation: stored.consultation !== false,
    zoning: typeof stored.zoning === 'boolean' ? stored.zoning : (!group && Boolean(user?.IsManager || user?.CanManageArticles)), modules: {} };
  for (const definition of modules) {
    const previous = stored.modules?.[definition.id];
    const initial = !group && legacyAccess(user, definition);
    result.modules[definition.id] = {
      full: definition.flag && !group ? Boolean(user?.[definition.flag]) : (previous?.full ?? initial),
      actions: Object.fromEntries(definition.actions.map(action => [action,
        previous ? previous.actions?.[action] === true : ((!definition.managerOnlyActions?.includes(action) || Boolean(user?.IsManager)) &&
          (initial || Boolean(definition.actionInheritedFlags?.[action]?.some(flag => user?.[flag]))))])),
    };
  }
  return result;
}

const assignedGroups = user => (user?.PermissionGroups || []).map(membership => membership.group || membership);

function effectivePermissionDetails(user) {
  const result = permissionDetails(user);
  for (const group of assignedGroups(user)) {
    const inherited = permissionDetails(group, true);
    // Consultation is a restriction: any enabled source keeps purchase prices hidden.
    result.consultation = result.consultation || inherited.consultation;
    result.zoning = result.zoning || inherited.zoning;
    for (const definition of modules) {
      const target = result.modules[definition.id];
      const source = inherited.modules[definition.id];
      target.full = target.full || source.full;
      for (const action of definition.actions) target.actions[action] = target.actions[action] || source.actions[action];
    }
  }
  // Manager access overrides the restriction without changing saved settings.
  if (user?.IsManager) result.consultation = false;
  return result;
}

function publicGroup(group) {
  return { id: group.id, Name: group.Name, IsDevisManager: Boolean(group.IsDevisManager),
    PermissionSettings: permissionDetails(group, true),
    ...(group._count ? { MemberCount: group._count.Users } : {}) };
}

function can(user, moduleId, action = 'read') {
  const definition = Object.prototype.hasOwnProperty.call(byId, moduleId) && byId[moduleId];
  if (!user || !definition || !definition.actions.includes(action)) return false;
  if (user.IsManager) return true;
  if (definition.managerOnlyActions?.includes(action)) return false;
  const details = effectivePermissionDetails(user);
  const grant = details.modules[moduleId];
  return Boolean(grant.full || grant.actions[action]);
}

function canZone(user) {
  return Boolean(user && (user.IsManager || (can(user, 'articles') && effectivePermissionDetails(user).zoning)));
}

function publicUser(user) {
  if (!user) return null;
  const { PasswordHash, Permissions, PermissionGroups, ArticleConsultation, ArticleZoning, ...safe } = user;
  const details = permissionDetails(user);
  const effective = effectivePermissionDetails(user);
  const groups = assignedGroups(user).map(publicGroup);
  return { ...safe, PermissionSettings: details,
    PermissionGroups: groups, PermissionGroupIds: groups.map(group => group.id),
    EffectiveIsDevisManager: Boolean(user.IsDevisManager || assignedGroups(user).some(group => group.IsDevisManager)),
    EffectivePermissions: Object.fromEntries(modules.map(m => [m.id,
      Object.fromEntries(m.actions.map(action => [action, Boolean(user.IsManager ||
        (!m.managerOnlyActions?.includes(action) && (effective.modules[m.id].full || effective.modules[m.id].actions[action])))]))])),
    CanAccessZoning: canZone(user),
  };
}

function validateDetails(value, user, group = false) {
  if (!value || value.version !== 1 || !value.modules || typeof value.modules !== 'object' || Array.isArray(value.modules)) {
    throw new Error('Format de permissions invalide.');
  }
  const result = permissionDetails(user, group);
  for (const key of Object.keys(value)) {
    if (!['version', 'consultation', 'zoning', 'modules'].includes(key)) throw new Error('Permission inconnue.');
  }
  for (const key of ['consultation', 'zoning']) {
    if (value[key] !== undefined) {
      if (typeof value[key] !== 'boolean') throw new Error('Une permission doit être un booléen.');
      result[key] = value[key];
    }
  }
  for (const [id, grant] of Object.entries(value.modules)) {
    if (!Object.prototype.hasOwnProperty.call(byId, id) || !grant || typeof grant !== 'object' || Array.isArray(grant)) throw new Error('Module inconnu.');
    if (Object.keys(grant).some(key => !['full', 'actions'].includes(key))) throw new Error('Permission inconnue.');
    if (grant.full !== undefined && typeof grant.full !== 'boolean') throw new Error('Permission générale invalide.');
    if (grant.actions !== undefined && (!grant.actions || typeof grant.actions !== 'object' || Array.isArray(grant.actions))) throw new Error('Actions invalides.');
    for (const [action, allowed] of Object.entries(grant.actions || {})) {
      if (!byId[id].actions.includes(action) || typeof allowed !== 'boolean') throw new Error('Action inconnue ou invalide.');
      result.modules[id].actions[action] = allowed;
    }
    if (grant.full !== undefined) result.modules[id].full = grant.full;
  }
  return result;
}

function permissionRows(settings, group = false) {
  return modules.flatMap(m => {
    const grant = settings.modules[m.id];
    const actions = [...((!m.flag || group) && grant.full ? ['FULL'] : []),
      ...m.actions.filter(action => grant.actions[action]).map(enumValue)];
    return actions.map(Action => ({ Module: enumValue(m.id), Action }));
  });
}

module.exports = { byId, legacyFlags, legacyAccess, enumValue, permissionDetails, effectivePermissionDetails, permissionRows, can, canZone, publicUser, publicGroup, validateDetails };
